import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const m = (v) => `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const q = (v) => Number(v || 0).toLocaleString('es-MX', { maximumFractionDigits: 1 });
const day = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' });
const pctCls = (v) => (v == null ? 'text-slate-400' : v < 15 ? 'text-rose-600' : v < 30 ? 'text-amber-600' : 'text-emerald-600');

const Kpi = ({ label, value, sub, tone = 'text-slate-900', testid }) => (
  <div className="rounded-lg border bg-white p-3" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`text-lg font-bold ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

const Th = ({ children, r }) => <th className={`px-3 py-2 text-[11px] uppercase text-slate-500 ${r ? 'text-right' : 'text-left'}`}>{children}</th>;
const Td = ({ children, r, cls = '' }) => <td className={`px-3 py-2 ${r ? 'text-right tabular-nums' : ''} ${cls}`}>{children}</td>;

const PricesTab = ({ d }) => (
  <div className="space-y-2">
    {d.cost <= 0 && <p className="text-xs bg-orange-50 border border-orange-200 text-orange-700 rounded p-2">Este producto no tiene costo capturado: la ganancia se calcula como si el costo fuera $0.</p>}
    <table className="w-full text-sm border rounded" data-testid="profit-prices-table">
      <thead className="bg-slate-50"><tr><Th>Precio</Th><Th r>Venta</Th><Th r>Comisión</Th><Th r>Costo</Th><Th r>Ganancia</Th><Th r>% s/venta</Th><Th r>% s/costo</Th></tr></thead>
      <tbody className="divide-y">
        {d.prices.map(p => (
          <tr key={p.key} data-testid={`profit-row-${p.key}`}>
            <Td cls="font-medium">{p.label}</Td><Td r>{m(p.sale)}</Td><Td r>{m(p.commission)}</Td><Td r>{m(p.cost)}</Td>
            <Td r cls={`font-bold ${p.profit < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{m(p.profit)}</Td>
            <Td r cls={`font-semibold ${pctCls(p.pct_sale)}`}>{p.pct_sale}%</Td>
            <Td r cls="font-semibold text-slate-700">{p.pct_cost == null ? '—' : `${p.pct_cost}%`}</Td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="text-[11px] text-slate-500">Ganancia = venta − comisión − costo. <b>% s/venta</b> = ganancia ÷ venta (cuánto de cada peso vendido te queda). <b>% s/costo</b> = ganancia ÷ costo (cuánto rinde lo que invertiste).</p>
  </div>
);

const PeriodsTab = ({ d }) => (
  <table className="w-full text-sm border rounded" data-testid="profit-periods-table">
    <thead className="bg-slate-50"><tr><Th>Periodo</Th><Th r>Tienda pzs</Th><Th r>Tienda $</Th><Th r>Envíos pzs</Th><Th r>Envíos $</Th><Th r>Total pzs</Th><Th r>Total $</Th></tr></thead>
    <tbody className="divide-y">
      {d.periods.map(p => (
        <tr key={p.days} data-testid={`profit-period-${p.days}`}>
          <Td cls="font-medium">{p.days === 7 ? 'Última semana' : p.days === 30 ? 'Último mes' : `Últimos ${p.days} días`}</Td>
          <Td r>{q(p.store.qty)}</Td><Td r>{m(p.store.amount)}</Td><Td r>{q(p.ship.qty)}</Td><Td r>{m(p.ship.amount)}</Td>
          <Td r cls="font-bold">{q(p.store.qty + p.ship.qty)}</Td><Td r cls="font-bold">{m(p.store.amount + p.ship.amount)}</Td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Bars = ({ title, rows, testid }) => {
  const max = Math.max(1, ...rows.map(r => r.qty));
  return (
    <div className="rounded-lg border p-3" data-testid={testid}>
      <div className="text-xs font-semibold text-slate-600 mb-2">{title}</div>
      {rows.length === 0 ? <p className="text-xs text-slate-400">Sin datos</p> : rows.map(r => (
        <div key={r.name || r.label} className="flex items-center gap-2 text-xs mb-1">
          <span className="w-24 truncate">{r.name || r.label}</span>
          <div className="flex-1 bg-slate-100 rounded h-3"><div className="bg-sky-500 h-3 rounded" style={{ width: `${(r.qty / max) * 100}%` }} /></div>
          <span className="w-10 text-right tabular-nums">{q(r.qty)}</span>
        </div>
      ))}
    </div>
  );
};

const PatternsTab = ({ d }) => (
  <div className="grid md:grid-cols-2 gap-3">
    <Bars title="Piezas por día de la semana (90 días)" rows={d.weekdays} testid="profit-weekdays" />
    <Bars title="Precio con el que más se vende" rows={d.price_mix} testid="profit-price-mix" />
    <Bars title="Vendedores que más lo venden" rows={d.top_sellers} testid="profit-top-sellers" />
    {d.has_variants && <Bars title="Variantes más vendidas" rows={d.variants} testid="profit-variants" />}
  </div>
);

const Summary = ({ d }) => {
  const t = d.totals_90;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
      <Kpi testid="kpi-best-day" label="Mayor día de venta" value={d.best_day ? `${q(d.best_day.qty)} pzs` : '—'} sub={d.best_day ? `${day(d.best_day.date)} · ${m(d.best_day.amount)}` : 'Sin ventas en 90 días'} />
      <Kpi testid="kpi-best-weekday" label="Mejor día de la semana" value={d.best_weekday?.name || '—'} sub={d.best_weekday ? `${q(d.best_weekday.qty)} pzs en 90 días` : ''} />
      <Kpi testid="kpi-last-sale" label="Última venta" value={d.days_since_last_sale == null ? 'Sin ventas' : d.days_since_last_sale === 0 ? 'Hoy' : `Hace ${d.days_since_last_sale} días`}
        tone={d.days_since_last_sale > 30 || d.days_since_last_sale == null ? 'text-rose-600' : 'text-slate-900'} />
      <Kpi testid="kpi-trend" label="Tendencia (30 vs 30 días antes)" value={d.trend_pct == null ? '—' : `${d.trend_pct > 0 ? '▲' : d.trend_pct < 0 ? '▼' : ''} ${Math.abs(d.trend_pct)}%`}
        tone={d.trend_pct > 0 ? 'text-emerald-600' : d.trend_pct < 0 ? 'text-rose-600' : 'text-slate-900'} sub={`Antes: ${q(d.prev30_qty)} pzs`} />
      <Kpi testid="kpi-stock" label={`Stock ${d.branch_name}`} value={`${q(d.stock)} pzs`} sub={`Vende ${q(d.avg_daily_30)} pzs/día (30 días)`} />
      <Kpi testid="kpi-days-stock" label="Te alcanza para" value={d.days_of_stock == null ? '—' : `${q(d.days_of_stock)} días`}
        tone={d.days_of_stock != null && d.days_of_stock < 7 ? 'text-rose-600' : 'text-slate-900'} sub="al ritmo de venta actual" />
      <Kpi testid="kpi-avg-price" label="Precio real promedio (90 días)" value={t.avg_price == null ? '—' : m(t.avg_price)} sub={`${q(t.qty)} pzs · ${m(t.amount)}`} />
      <Kpi testid="kpi-real-profit" label="Ganancia real (90 días)" value={t.real_profit == null ? 'Sin costo' : m(t.real_profit)}
        tone={t.real_profit == null ? 'text-slate-400' : t.real_profit < 0 ? 'text-rose-600' : 'text-emerald-700'} sub={`Comisiones pagadas: ${m(t.commission)}`} />
    </div>
  );
};

export const ProfitStatsDialog = ({ product, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!product) return;
    setD(null);
    axios.get(`${API}/products/${product.id}/profit-stats`, { withCredentials: true })
      .then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Error al cargar ganancia'));
  }, [product]);

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="profit-stats-dialog">
        <DialogHeader><DialogTitle>💹 Ganancia y estadísticas · {product?.name}</DialogTitle></DialogHeader>
        {!d ? <p className="text-sm text-muted-foreground py-8 text-center">Cargando…</p> : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm bg-slate-50 rounded-lg p-3" data-testid="profit-header">
              <span>Nombre: <b>{d.name}</b></span><span>SKU: <b>{d.sku || '—'}</b></span>
              <span>Comisión: <b>{m(d.commission)}</b></span><span>Costo: <b>{d.cost > 0 ? m(d.cost) : 'Sin capturar'}</b></span>
            </div>
            <Summary d={d} />
            <Tabs defaultValue="prices">
              <TabsList>
                <TabsTrigger value="prices" data-testid="profit-tab-prices">Ganancia por precio</TabsTrigger>
                <TabsTrigger value="periods" data-testid="profit-tab-periods">Ventas por periodo</TabsTrigger>
                <TabsTrigger value="patterns" data-testid="profit-tab-patterns">Patrones de venta</TabsTrigger>
              </TabsList>
              <TabsContent value="prices"><PricesTab d={d} /></TabsContent>
              <TabsContent value="periods"><PeriodsTab d={d} /></TabsContent>
              <TabsContent value="patterns"><PatternsTab d={d} /></TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
